import { fundFamilyConfigurationCreditFacilityRoutes } from "features/credit-facility/CreditFacilityRoutes";
import { fundFamilyConfigurationEntitiesRoutes } from "features/fund-family/configuration/entities/FundFamilyConfigurationEntitiesRoutes";
import { fundFamilyConfigurationFundRoutes } from "features/fund-family/configuration/fund/FundFamilyConfigurationFundRoutes";
import { fundFamilyConfigurationFundStructureRoutes } from "features/fund-family/configuration/fund-structure/FundFamilyConfigurationFundStructureRoutes";
import { fundFamilyConfigurationNoticeRoutes } from "features/fund-family/configuration/notices/FundFamilyConfigurationNoticeRoutes";
import { createInvestorEntityRoutes } from "features/investor-entities/InvestorEntityRoutes";
import { getCommitmentInformationTableDataRetrieveLoader } from "gen/endpoints/commitment-information-table-data/commitment-information-table-data";
import { getFeeBasisCategoriesListLoader } from "gen/endpoints/fee-basis-category/fee-basis-category";
import { getFeeInformationListLoader } from "gen/endpoints/fee-information/fee-information";
import { getFundListLoader } from "gen/endpoints/fund-entities/fund-entities";
import { getInvestorClassRetrieveLoader } from "gen/endpoints/investor-class/investor-class";
import { getInvestorClassInformationListLoader } from "gen/endpoints/investor-class-information/investor-class-information";
import {
  getTransactionCalculationAndFeeBasisListLoader,
  getTransactionCalculationListLoader,
} from "gen/endpoints/transactions/transactions";
import { getWaterfallConfigRetrieveLoader } from "gen/endpoints/waterfall/waterfall";
import i18n from "i18n/config";
import { redirect, RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const fundFamilyConfigurationRoutes: RouteObject = {
  id: "fundFamilyConfiguration",
  path: "configuration",
  children: [
    {
      id: "fundFamilyConfigurationIndex",
      index: true,
      loader: () => redirect("fund"),
    },
    {
      id: "fundFamilyConfigurationCalculations",
      path: "calculations",
      children: [
        {
          id: "fundFamilyConfigurationCalculationsIndex",
          index: true,
          handle: {
            showSidebar: true,
            description: "Configuration - Calculations",
          },
          loader: composeLoaders(
            getTransactionCalculationListLoader(),
            getTransactionCalculationAndFeeBasisListLoader(),
            getFeeBasisCategoriesListLoader(),
          ),
          lazy: async () => {
            const { CalculationsPage } = await import(
              "features/calculation/CalculationsPage"
            );
            return { Component: CalculationsPage };
          },
        },
        {
          id: "fundFamilyConfigurationCalculationsCreate",
          path: "create",
          lazy: async () => {
            const { CalculationCreatePage } = await import(
              "features/calculation/CalculationCreatePage"
            );
            return { Component: CalculationCreatePage };
          },
        },
        {
          id: "fundFamilyConfigurationCalculationsEdit",
          path: ":transactionCalculationId",
          lazy: async () => {
            const { CalculationEditPage } = await import(
              "features/calculation/CalculationEditPage"
            );
            return { Component: CalculationEditPage };
          },
        },
      ],
    },
    {
      id: "fundFamilyConfigurationTransactionCodes",
      path: "transaction-codes",
      children: [
        {
          id: "transactionCodesIndex",
          index: true,
          lazy: async () => {
            const { TransactionCodesPage } = await import(
              "features/fund-family/configuration/fund/txcodes/TransactionCodesPage"
            );
            return {
              Component: TransactionCodesPage,
            };
          },
          handle: {
            title: i18n.t("fundFamily:setup.transactionCodes.title"),
            showSidebar: true,
          },
        },
        {
          id: "transactionCodesEdit",
          path: "edit",
          lazy: async () => {
            const { TransactionCodesEditPage } = await import(
              "features/fund-family/configuration/fund/txcodes/TransactionCodesEditPage"
            );
            return { Component: TransactionCodesEditPage };
          },
        },
      ],
    },
    {
      id: "fundFamilyConfigurationClasses",
      path: "classes",
      children: [
        {
          id: "fundFamilyConfigurationClassesIndex",
          index: true,
          handle: {
            showSidebar: true,
            description: "Configuration - Investor/Commitment Classes",
          },
          lazy: async () => {
            const { CommitmentClassesPage } = await import(
              "features/commitment-classes/CommitmentClassesPage"
            );
            return { Component: CommitmentClassesPage };
          },
          loader: getInvestorClassInformationListLoader(),
        },
        {
          id: "fundFamilyConfigurationClassesEdit",
          path: ":investorClassId/*",
          lazy: async () => {
            const { CommitmentClassSetupPage } = await import(
              "features/commitment-classes/setup/CommitmentClassSetupPage"
            );
            return { Component: CommitmentClassSetupPage };
          },
          children: [
            {
              id: "basic",
              path: "",
              lazy: async () => {
                const { CommitmentClassTabBasicDetails } = await import(
                  "features/commitment-classes/setup/CommitmentClassTabBasicDetails"
                );
                return { Component: CommitmentClassTabBasicDetails };
              },
              loader: composeLoaders(
                getInvestorClassRetrieveLoader(
                  ({ fundFamilyId, investorClassId }) => ({
                    fundFamilyId: fundFamilyId!,
                    investorClassId: investorClassId!,
                  }),
                ),
                getFundListLoader(),
              ),
              handle: {
                title: i18n.t("common:basicDetails"),
              },
            },
            {
              id: "fundFamilyConfigurationClassesEditFees",
              path: "fees",
              lazy: async () => {
                const { CommitmentClassTabFees } = await import(
                  "features/commitment-classes/setup/CommitmentClassTabFees"
                );
                return { Component: CommitmentClassTabFees };
              },
              loader: composeLoaders(
                getFeeInformationListLoader(({ investorClassId }) => ({
                  investor_class_id: investorClassId,
                })),
                getFundListLoader(),
              ),
              handle: {
                title: i18n.t("common:fees"),
              },
            },
            {
              id: "fundFamilyConfigurationClassesEditWaterfall",
              path: "waterfall",
              lazy: async () => {
                const { CommitmentClassTabWaterfall } = await import(
                  "features/commitment-classes/setup/CommitmentClassTabWaterfall"
                );
                return { Component: CommitmentClassTabWaterfall };
              },
              loader: composeLoaders(
                getWaterfallConfigRetrieveLoader(({ investorClassId }) => ({
                  investor_class_id: investorClassId,
                })),
                getFundListLoader(),
              ),
              handle: {
                title: i18n.t("common:waterfall"),
              },
            },
            {
              id: "fundFamilyConfigurationClassesEditCommitments",
              path: "commitments",
              lazy: async () => {
                const { CommitmentClassTabCommitments } = await import(
                  "features/commitment-classes/setup/CommitmentClassTabCommitments"
                );
                return { Component: CommitmentClassTabCommitments };
              },
              loader: getCommitmentInformationTableDataRetrieveLoader(),
              handle: {
                title: i18n.t("common:commitments"),
              },
            },
          ],
        },
        {
          id: "fundFamilyConfigurationClassesFee",
          path: ":investorClassId/fees/:feeId",
          lazy: async () => {
            const { CommitmentClassFeePage } = await import(
              "features/commitment-classes/setup/CommitmentClassFeePage"
            );
            return { Component: CommitmentClassFeePage };
          },
        },
        {
          id: "fundFamilyConfigurationClassesFeeOverride",
          path: ":investorClassId/fees/:feeId/override",
          lazy: async () => {
            const { CommitmentClassSetupFeeOverridePage } = await import(
              "features/commitment-classes/setup/CommitmentClassSetupFeeOverridePage"
            );
            return { Component: CommitmentClassSetupFeeOverridePage };
          },
        },
      ],
    },
    fundFamilyConfigurationFundRoutes,
    fundFamilyConfigurationFundStructureRoutes,
    fundFamilyConfigurationEntitiesRoutes,
    fundFamilyConfigurationNoticeRoutes,
    fundFamilyConfigurationCreditFacilityRoutes,
    createInvestorEntityRoutes("fundFamilyConfiguration"),
  ],
};
